import { FC, useState, lazy, Suspense } from 'react';
import { graphql, useFragment, useRelayEnvironment } from 'react-relay';
import { FormattedMessage } from 'dibs-react-intl';
import PhoneIcon from 'dibs-icons/exports/legacy/Phone';
import { Link } from 'dibs-elements/exports/Link';
import { IconWrapper } from 'dibs-elements/exports/IconWrapper';

import { sharedLoginCheck } from '../../authentication/sharedLoginCheck';
import { isEmailOnly } from 'dibs-cookie-jar/exports/isEmailOnly';
import authModalLoader from 'dibs-buyer-layout/src/utils/AuthModalLoader';

import styles from './PdpSharedCallDealerCta.scss';

import { PdpSharedCallDealerCta_item$key } from './__generated__/PdpSharedCallDealerCta_item.graphql';
import { PdpSharedCallDealerCta_user$key } from './__generated__/PdpSharedCallDealerCta_user.graphql';

const CallDealerModalLazy = lazy(
    () =>
        import(
            /* webpackChunkName: "PdpSharedCallDealerModalWrapper" */ './PdpSharedCallDealerModal/PdpSharedCallDealerModalWrapper'
        )
);

type Props = {
    item: PdpSharedCallDealerCta_item$key | null | undefined;
    user: PdpSharedCallDealerCta_user$key | null | undefined;
    size?: 'small' | 'medium';
    containerClassName?: string;
};

export const PdpSharedCallDealerCta: FC<Props> = ({
    item: itemRef,
    user: userRef,
    size = 'small',
    containerClassName = styles.contactDealer,
}) => {
    const item = useFragment(
        graphql`
            fragment PdpSharedCallDealerCta_item on Item {
                serviceId
                seller {
                    trackedPhone {
                        phoneNumber
                    }
                }
            }
        `,
        itemRef
    );

    const user = useFragment(
        graphql`
            fragment PdpSharedCallDealerCta_user on User {
                profile {
                    email
                }
            }
        `,
        userRef
    );

    const [isModalOpen, setIsModalOpen] = useState(false);
    const [userId, setUserId] = useState<string | null>(null);

    const environment = useRelayEnvironment();

    const onAuthSuccess = (authPayload: { userId: string }): void => {
        setIsModalOpen(true);
        setUserId(authPayload.userId);
    };

    const onCallDealerClick = (): void => {
        const ga = { label: 'call seller link' };
        const emailOnly = isEmailOnly(document.cookie);
        if (emailOnly) {
            const email = user?.profile?.email;
            authModalLoader
                .show({
                    action: authModalLoader.constants.REQUEST_PASSWORD_EMAIL,
                    flow: authModalLoader.constants.CREATE_PASSWORD_CALL_SELLER_FLOW,
                    email,
                    relayEnvironment: environment,
                    ga,
                })
                .then((authPayload: { userId: string | null } | null) => {
                    if (authPayload?.userId) {
                        onAuthSuccess({ userId: authPayload.userId });
                    }
                });
        } else {
            sharedLoginCheck({
                onSuccess: onAuthSuccess,
                authOptions: {
                    flow: authModalLoader.constants.CALL_DEALER_FLOW,
                    ga,
                },
            });
        }
    };

    const telephone = item?.seller?.trackedPhone?.phoneNumber;

    if (!telephone) {
        return null;
    }

    // userId check was added not to crash on serverside rendering, because 'document' object does not exist.
    const emailOnly = userId && isEmailOnly(document.cookie);
    const itemId = item?.serviceId || '';

    return (
        <div className={containerClassName}>
            <Link
                buyerLinkType="standardLink"
                className={`${styles.link} ${styles[size]}`}
                onClick={onCallDealerClick}
                dataTn="call-seller"
            >
                <div className={styles.phoneIconWrapper}>
                    <IconWrapper
                        wrapperClass={styles.iconWrapper}
                        icon={<PhoneIcon className={`${styles.phoneIcon} ${styles[size]}`} />}
                        borderWeight="thin"
                        size={30}
                    />
                </div>
                <FormattedMessage
                    id="PdpSharedCallDealerCta.cta_link"
                    defaultMessage="Call seller through 1stDibs"
                />
            </Link>
            {userId && !emailOnly && isModalOpen && (
                <Suspense fallback="">
                    <CallDealerModalLazy
                        isOpen
                        userId={userId}
                        itemId={itemId}
                        handleCloseClick={() => setIsModalOpen(false)}
                    />
                </Suspense>
            )}
        </div>
    );
};

// lazy load
export default PdpSharedCallDealerCta;
